import {
  ChangeLog,
  Response,
  TransactionWarning,
  apiAbortController,
} from '@ui/src';
import api from '../../../api/api';
import {
  NewApplicationListItem,
  NewApplicationValues,
  NewApplicationsValidatorResponse,
  ApplicationComment,
  FormValues,
  SystemLogs,
  CorporateFormValues,
  NewApplicationCorporateInfo,
  CompanyOfficialFormValues,
  AssignCompanyOfficialFormValues,
  CofApprovalStatus,
  NewApplicationChildrenInfo,
  ApplicationDocument,
  ApplicationDocumentStatus,
  ApplicationAgreement,
  Channel,
  AllowedCategory,
  DictionaryItem,
  AccountsCurrencies,
  AccountsCurrenciesFormValues,
  AccountsFlows,
  AccountsFlowsFormValues,
  ExpectedFundsOrigins,
  ExpectedFundsOriginsFormValues,
  CRSAndFatca,
  CRSAndFatcaFormValues,
  AffilatedCompany,
  AffilatedCompanyFormValues,
  Client,
} from '../types';
import { Employee } from '../../../store/dictionaries/types';
import {
  LockAuditLog,
  Lock,
  LockFormValues,
  COFAndIndividualQuestionnarieFormValues,
} from '../../individual/types';
import {
  CustomerScoreQueuedProps,
  CustomerScore,
  Questionnarie,
  OngoingMonitring,
  OngoingCheckingStatus,
} from '../../common/types';
import { CorporateQuestionnarieFormValues } from '../../corporate/types';

export const applicationsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getNewApplicationsList: build.query<
      Response<NewApplicationListItem>,
      string
    >({
      query: (query) => ({
        url: `/v1/clients-applications${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getNewApplicationsValidators: build.query<
      { data: NewApplicationsValidatorResponse },
      void
    >({
      query: () => ({
        url: '/v1/clients-applications/validators',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createApplication: build.mutation<
      {
        data: NewApplicationListItem;
      },
      NewApplicationValues
    >({
      query: (body) => ({
        url: '/v1/clients-applications/individuals',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    addNewApplicationComment: build.mutation<
      { data: ApplicationComment },
      { body: { text: string }; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/${data.id}/comments`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateNewApplicationComment: build.mutation<
      { data: ApplicationComment },
      { body: { text: string }; id: string; commentId: string }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/${data.id}/comments/${data.commentId}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteNewApplicationComment: build.mutation<
      { data: ApplicationComment },
      { id: string; commentId: string }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/${data.id}/comments/${data.commentId}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getNewApplicationInfo: build.query<
      { data: NewApplicationListItem },
      string
    >({
      query: (id) => ({
        url: `/v1/clients-applications/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getNewApplicationOrganization: build.query<
      { data: NewApplicationListItem },
      string
    >({
      query: (id) => ({
        url: `/v1/clients-applications/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getNewApplicationCorporateOrganization: build.query<
      { data: NewApplicationCorporateInfo },
      string
    >({
      query: (id) => ({
        url: `/v1/clients-applications/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getNewApplicationDefaultDocumentCategories: build.query<
      { data: AllowedCategory[] },
      string
    >({
      query: (id) => ({
        url: `/v1/clients-applications/${id}/documents/allowed-categories`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getNewApplicationSystemLogs: build.query<Response<SystemLogs>, string>({
      query: (id) => ({
        url: `/v1/clients-creations-logs?all=true&applicationId=${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateNewApplication: build.mutation<
      { data: NewApplicationListItem },
      { body: FormValues; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/individuals/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateReviewStatus: build.mutation<
      { data: NewApplicationListItem },
      { body: { reviewStatus: string }; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/individuals/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    sumSubTestReview: build.mutation<
      {
        ok: number;
      },
      {
        body: {
          rejectType: string;
          reviewAnswer: string;
        };
        id: string;
      }
    >({
      query: (data) => ({
        url: `/v1/sumsub/applications/${data.id}/test-review`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateIndividualApplicationStatus: build.mutation<
      { data: NewApplicationListItem },
      { body: { status: string; reviewEmployeeId?: string }; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/individuals/${data.id}/status`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCompanyOfficialApplication: build.mutation<
      { data: NewApplicationListItem },
      { body: FormValues; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/company-officials/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCompanyOfficialStatus: build.mutation<
      { data: NewApplicationListItem },
      { body: { status: string; reviewEmployeeId?: string }; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/company-officials/${data.id}/status`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getNewApplicationCorporateInfo: build.query<
      { data: NewApplicationCorporateInfo },
      string
    >({
      query: (id) => ({
        url: `/v1/clients-applications/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCorporateApplicationSystemLogs: build.query<
      Response<SystemLogs>,
      string
    >({
      query: (id) => ({
        url: `/v1/clients-creations-logs?all=true&applicationId=${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createCorporateApplication: build.mutation<
      {
        data: NewApplicationListItem;
      },
      CorporateFormValues
    >({
      query: (body) => ({
        url: '/v1/clients-applications/corporates',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCorporateApplication: build.mutation<
      {
        data: NewApplicationCorporateInfo;
      },
      { body: CorporateFormValues; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/corporates/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createCompanyOfficial: build.mutation<
      {
        data: NewApplicationListItem;
      },
      { body: CompanyOfficialFormValues; id: string }
    >({
      query: (data) => ({
        url: '/v1/clients-applications/company-officials',
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    assignCompanyOfficial: build.mutation<
      {
        data: NewApplicationChildrenInfo;
      },
      { body: AssignCompanyOfficialFormValues; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/corporates/${data.id}/assign-company-official`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateAppToApp: build.mutation<
      {
        data: NewApplicationChildrenInfo;
      },
      {
        body: CompanyOfficialFormValues;
        id: string;
        appToAppId: string;
      }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/corporates/${data.id}/company-officials/${data.appToAppId}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateAppToAppStatus: build.mutation<
      {
        data: NewApplicationChildrenInfo;
      },
      {
        body: {
          status: CofApprovalStatus;
        };
        id: string;
        appToAppId: string;
      }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/corporates/${data.id}/company-officials/${data.appToAppId}/status`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCompanyOfficial: build.mutation<
      {
        data: NewApplicationListItem;
      },
      {
        body: CompanyOfficialFormValues;
        id: string;
        officialApplicationId: string;
      }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/${data.id}/company-officials/${data.officialApplicationId}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCorporateReviewStatus: build.mutation<
      { data: NewApplicationCorporateInfo },
      { body: { reviewStatus: string }; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/corporates/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCorporateApplicationStatus: build.mutation<
      { data: NewApplicationCorporateInfo },
      { body: { status: string; reviewEmployeeId?: string }; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/corporates/${data.id}/status`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersApplicationsList: build.query<
      Response<NewApplicationListItem>,
      string
    >({
      query: (query) => ({
        url: `/v1/clients-applications${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIndividualNewApplicationDocuments: build.query<Response<any>, string>({
      query: (id) => ({
        url: `/v1/clients-applications/individuals/${id}/documents?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIndividualNewApplicationDocument: build.query<
      { data: ApplicationDocument },
      { id: string; documentId: string }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/individuals/${data.id}/documents/${data.documentId}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIndividualApplicationDocumentFile: build.query<
      { data: any },
      { id: string; documentId: string }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/individuals/${data.id}/documents/${data.documentId}/file`,
        responseHandler: (response) => response.blob(),
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCofNewApplicationDocuments: build.query<Response<any>, string>({
      query: (id) => ({
        url: `/v1/clients-applications/company-officials/${id}/documents?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCofNewApplicationDocument: build.query<
      { data: ApplicationDocument },
      { id: string; documentId: string }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/company-officials/${data.id}/documents/${data.documentId}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCofApplicationDocumentFile: build.query<
      { data: any },
      { id: string; documentId: string }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/company-officials/${data.id}/documents/${data.documentId}/file`,
        responseHandler: (response) => response.blob(),
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    addCorporateApplicationDocuments: build.mutation<
      {
        data: ApplicationDocument;
      },
      {
        id: string;
        body: {
          uploadId: string;
          name: string;
          documentCategoryId?: string;
          expirationDate?: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/corporates/${data.id}/documents`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    addIndividualApplicationDocuments: build.mutation<
      {
        data: ApplicationDocument;
      },
      {
        id: string;
        body: {
          uploadId: string;
          name: string;
          documentCategoryId?: string;
          expirationDate?: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/individuals/${data.id}/documents`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    addCofApplicationDocuments: build.mutation<
      {
        data: ApplicationDocument;
      },
      {
        id: string;
        body: {
          uploadId: string;
          name: string;
          documentCategoryId?: string;
          expirationDate?: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/company-officials/${data.id}/documents`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCorporateNewApplicationDocuments: build.query<
      Response<ApplicationDocument>,
      string
    >({
      query: (id) => ({
        url: `/v1/clients-applications/corporates/${id}/documents?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCorporateNewApplicationDocument: build.query<
      { data: ApplicationDocument },
      { id: string; documentId: string }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/corporates/${data.id}/documents/${data.documentId}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCorporateApplicationDocumentFile: build.query<
      { data: any },
      { id: string; documentId: string }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/corporates/${data.id}/documents/${data.documentId}/file`,
        responseHandler: (response) => response.blob(),
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    changeCorporateApplicationDocumentStatus: build.mutation<
      any,
      { body: { ids: string[]; status: ApplicationDocumentStatus }; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/corporates/${data.id}/documents/batch-update-status`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    changeCorporateApplicationDocumentCategory: build.mutation<
      any,
      { body: { ids: string[]; documentCategoryId: string }; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/corporates/${data.id}/documents/batch-update-category`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    batchDeleteCorporateApplicationDocuments: build.mutation<
      {
        data: ApplicationDocument;
      },
      {
        id: string;
        documentId: string[];
      }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/corporates/${data.id}/batch-delete`,
        method: 'DELETE',
        body: {
          documentId: data.documentId,
        },
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    batchDeleteIndividualApplicationDocuments: build.mutation<
      {
        data: ApplicationDocument;
      },
      {
        id: string;
        documentId: string[];
      }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/individuals/${data.id}/batch-delete`,
        method: 'DELETE',
        body: {
          documentId: data.documentId,
        },
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    batchDeleteCOFApplicationDocuments: build.mutation<
      {
        data: ApplicationDocument;
      },
      {
        id: string;
        documentId: string[];
      }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/company-officials/${data.id}/batch-delete`,
        method: 'DELETE',
        body: {
          documentId: data.documentId,
        },
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getApplicationAgreements: build.query<
      Response<ApplicationAgreement>,
      string
    >({
      query: (id) => ({
        url: `/v1/clients-applications/${id}/agreements`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersAgreementFile: build.query<{ data: any }, string>({
      query: (id) => ({
        url: `/v1/agreements/${id}/file`,
        responseHandler: (response) => response.blob(),
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersCurrentEmployee: build.query<{ data: Employee }, void>({
      query: () => ({
        url: '/v1/employees/whoami',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getApplicationChat: build.query<Response<Channel>, string>({
      query: (id) => ({
        url: `/v1/channels?applicationId=${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getApplicationAgreement: build.query<
      { data: ApplicationAgreement },
      string
    >({
      query: (id) => ({
        url: `/v1/agreements/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    setReviewEmployee: build.mutation<
      { data: NewApplicationListItem },
      { body: { reviewEmployeeId: string }; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/${data.id}/review-employee`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    changeNewApplicationDocumentExpirationDate: build.mutation<
      { data: ApplicationDocument },
      { id: string; body: { id: string; expirationDate: string } }
    >({
      query: (data) => ({
        url: `/v1/documents/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getApplicationOngoingMonitoring: build.query<
      Response<OngoingMonitring>,
      string
    >({
      query: (id) => ({
        url: `/v1/clients-applications/${id}/warnings-groups?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getApplicationOngoingStatus: build.query<
      Response<OngoingCheckingStatus>,
      string
    >({
      query: (id) => ({
        url: `/v1/clients-blacklists-queue-items?applicationId=${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    restartOngoingChecking: build.mutation<
      { data: OngoingCheckingStatus },
      {
        applicationId: string;
      }
    >({
      query: (body) => ({
        url: '/v1/clients-blacklists-queue-items',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    applyApplicationOngoingWarningAction: build.mutation<
      { data: TransactionWarning },
      {
        appId: string;
        id: string;
        warningId: string;
        body: {
          action: string;
          comment: string;
          employeeId: string;
          matchStatus?: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/${data.appId}/warnings-groups/${data.id}/warnings/${data.warningId}/action`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    batchApplyApplicationOngoingWarningAction: build.mutation<
      { data: any },
      {
        appId: string;
        body: {
          warningGroupId: string;
          action: string;
          comment: string;
          employeeId: string;
          matchStatus?: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/${data.appId}/warnings-groups/warnings/batch-action`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    submitApplication: build.mutation<any, string>({
      query: (id) => ({
        url: `/v1/clients-applications/${id}/submit`,
        method: 'PUT',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getApplicationQuestionnarie: build.query<{ data: Questionnarie }, string>({
      query: (id) => ({
        url: `/v1/clients-applications/${id}/questionnaire`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCOFQuestionnarie: build.mutation<
      { data: Questionnarie },
      {
        id: string;
        body: COFAndIndividualQuestionnarieFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/company-officials/${data.id}/questionnaire`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateInividualQuestionnarie: build.mutation<
      { data: Questionnarie },
      {
        id: string;
        body: COFAndIndividualQuestionnarieFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/individuals/${data.id}/questionnaire`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCorporateQuestionnarie: build.mutation<
      { data: Questionnarie },
      {
        id: string;
        body: CorporateQuestionnarieFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/corporates/${data.id}/questionnaire`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    renameCorporateDocument: build.mutation<
      {
        data: any;
      },
      {
        id: string;
        documentId: string;
        body: {
          name: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/corporates/${data.id}/documents/${data.documentId}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    renameCofDocument: build.mutation<
      {
        data: any;
      },
      {
        id: string;
        documentId: string;
        body: {
          name: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/company-officials/${data.id}/documents/${data.documentId}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    renameIndividualDocument: build.mutation<
      {
        data: any;
      },
      {
        id: string;
        documentId: string;
        body: {
          name: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/individuals/${data.id}/documents/${data.documentId}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getApplicationChangeLogs: build.query<Response<ChangeLog>, string>({
      query: (query) => ({
        url: `/v1/audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    resetApplicationWarningAction: build.mutation<
      { data: TransactionWarning },
      {
        appId: string;
        id: string;
        warningId: string;
      }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/${data.appId}/warnings-groups/${data.id}/warnings/${data.warningId}/reset`,
        method: 'PUT',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getApplicationWarningGroupWithPagination: build.query<
      Response<TransactionWarning>,
      { id: string; warningGroupId: string; cursor?: string }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/${data.id}/warnings-groups/${data.warningGroupId}/warnings?cursor=${data.cursor}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    resetApplicationClientPassword: build.mutation<
      { data: { token: string } },
      string
    >({
      query: (id) => ({
        url: `/v1/recover`,
        method: 'POST',
        body: {
          userId: id,
        },
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getApplicationClientAuditLogs: build.query<Response<LockAuditLog>, string>({
      query: (query) => ({
        url: `/v1/idm-audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getRelationShipToCustomerDictionary: build.query<
      Response<DictionaryItem>,
      void
    >({
      query: () => ({
        url: '/v1/dictionaries/2f06e96f-b4cb-4112-9da6-4807b47f018d/items?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getSourceOfWealthForCorporateDictionary: build.query<
      Response<DictionaryItem>,
      void
    >({
      query: () => ({
        url: '/v1/dictionaries/d16a207a-0909-41ee-87e6-5e4b88942bc5/items?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getSourceOfWealthForIndividualDictionary: build.query<
      Response<DictionaryItem>,
      void
    >({
      query: () => ({
        url: '/v1/dictionaries/66ee818d-132c-4a6e-b2ba-fea3405145f4/items?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getSourceOfIncomeDictionary: build.query<Response<DictionaryItem>, void>({
      query: () => ({
        url: '/v1/dictionaries/be30b804-9984-4281-a712-d9af90e310cc/items?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getBusinessActivitiesDictionary: build.query<
      Response<DictionaryItem>,
      void
    >({
      query: () => ({
        url: '/v1/dictionaries/368d47fb-2b48-415a-8abe-464f24747f84/items?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersMainClientsDictionary: build.query<
      Response<DictionaryItem>,
      void
    >({
      query: () => ({
        url: '/v1/dictionaries/bb747e8c-4501-40b4-865e-21abfdc890e4/items?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getSalesChannelsDictionary: build.query<Response<DictionaryItem>, void>({
      query: () => ({
        url: '/v1/dictionaries/822216b4-37cf-472b-b2fb-677c036dea5b/items?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerSourceOfWealth: build.query<Response<DictionaryItem>, void>({
      query: () => ({
        url: '/v1/dictionaries/f27068ee-8e93-41f1-a2bc-4d5ab6f42a40/items?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerSourceOfFunds: build.query<Response<DictionaryItem>, void>({
      query: () => ({
        url: '/v1/dictionaries/8d4eef94-ea66-4467-a8f8-bde787910cde/items?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAnnualIncomeDictionary: build.query<Response<DictionaryItem>, void>({
      query: () => ({
        url: '/v1/dictionaries/dcda46d0-c183-44fb-b0a7-1dab5797ee71/items?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTypeOfAccountsDictionary: build.query<Response<DictionaryItem>, void>({
      query: () => ({
        url: '/v1/dictionaries/26e90867-d430-437a-901f-84464e6ad26b/items?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountsCurrencies: build.query<Response<AccountsCurrencies>, string>({
      query: (id) => ({
        url: `/v1/questionnaires-corporates/${id}/accounts-currencies?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createAccountsCurrency: build.mutation<
      { data: AccountsCurrencies },
      {
        id: string;
        body: AccountsCurrenciesFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-corporates/${data.id}/accounts-currencies`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateAccountsCurrency: build.mutation<
      { data: AccountsCurrencies },
      {
        id: string;
        body: AccountsCurrenciesFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-corporates/accounts-currencies/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteAccountsCurrency: build.mutation<
      { data: AccountsCurrencies },
      string
    >({
      query: (id) => ({
        url: `/v1/questionnaires-corporates/accounts-currencies/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountsFlows: build.query<Response<AccountsFlows>, string>({
      query: (id) => ({
        url: `/v1/questionnaires-corporates/${id}/accounts-flows?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createAccountsFlow: build.mutation<
      { data: AccountsFlows },
      {
        id: string;
        body: AccountsFlowsFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-corporates/${data.id}/accounts-flows`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateAccountsFlow: build.mutation<
      { data: AccountsFlows },
      {
        id: string;
        body: AccountsFlowsFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-corporates/accounts-flows/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteAccountsFlow: build.mutation<{ data: AccountsFlows }, string>({
      query: (id) => ({
        url: `/v1/questionnaires-corporates/accounts-flows/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getExpectedFundsOrigins: build.query<
      Response<ExpectedFundsOrigins>,
      string
    >({
      query: (id) => ({
        url: `/v1/questionnaires-corporates/${id}/expected-funds-origins?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createExpectedFundsOrigins: build.mutation<
      { data: ExpectedFundsOrigins },
      {
        id: string;
        body: ExpectedFundsOriginsFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-corporates/${data.id}/expected-funds-origins`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateExpectedFundsOrigins: build.mutation<
      { data: ExpectedFundsOrigins },
      {
        id: string;
        body: ExpectedFundsOriginsFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-corporates/expected-funds-origins/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteExptectedFundsOrigins: build.mutation<
      { data: ExpectedFundsOrigins },
      string
    >({
      query: (id) => ({
        url: `/v1/questionnaires-corporates/expected-funds-origins/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIndividualAccountsCurrencies: build.query<
      Response<AccountsCurrencies>,
      string
    >({
      query: (id) => ({
        url: `/v1/questionnaires-individual/${id}/accounts-currencies?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createIndividualAccountsCurrency: build.mutation<
      { data: AccountsCurrencies },
      {
        id: string;
        body: AccountsCurrenciesFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-individual/${data.id}/accounts-currencies`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateIndividualAccountsCurrency: build.mutation<
      { data: AccountsCurrencies },
      {
        id: string;
        body: AccountsCurrenciesFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-individual/accounts-currencies/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteIndividualAccountsCurrency: build.mutation<
      { data: AccountsCurrencies },
      string
    >({
      query: (id) => ({
        url: `/v1/questionnaires-individual/accounts-currencies/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIndividualAccountsFlows: build.query<Response<AccountsFlows>, string>({
      query: (id) => ({
        url: `/v1/questionnaires-individual/${id}/accounts-flows?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createIndividualAccountsFlow: build.mutation<
      { data: AccountsFlows },
      {
        id: string;
        body: AccountsFlowsFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-individual/${data.id}/accounts-flows`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateIndividualAccountsFlow: build.mutation<
      { data: AccountsFlows },
      {
        id: string;
        body: AccountsFlowsFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-individual/accounts-flows/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteIndividualAccountsFlow: build.mutation<
      { data: AccountsFlows },
      string
    >({
      query: (id) => ({
        url: `/v1/questionnaires-individual/accounts-flows/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIndividualExpectedFundsOrigins: build.query<
      Response<ExpectedFundsOrigins>,
      string
    >({
      query: (id) => ({
        url: `/v1/questionnaires-individual/${id}/expected-funds-origins?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createIndividualExpectedFundsOrigins: build.mutation<
      { data: ExpectedFundsOrigins },
      {
        id: string;
        body: ExpectedFundsOriginsFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-individual/${data.id}/expected-funds-origins`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateIndividualExpectedFundsOrigins: build.mutation<
      { data: ExpectedFundsOrigins },
      {
        id: string;
        body: ExpectedFundsOriginsFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-individual/expected-funds-origins/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteIndividualExptectedFundsOrigins: build.mutation<
      { data: ExpectedFundsOrigins },
      string
    >({
      query: (id) => ({
        url: `/v1/questionnaires-individual/expected-funds-origins/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCRSAndFacta: build.query<Response<CRSAndFatca>, string>({
      query: (id) => ({
        url: `/v1/questionnaires-individual/${id}/crs-and-fatca?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createCRSAndFacta: build.mutation<
      { data: CRSAndFatca },
      {
        id: string;
        body: CRSAndFatcaFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-individual/${data.id}/crs-and-fatca`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCRSAndFacta: build.mutation<
      { data: CRSAndFatca },
      {
        id: string;
        body: CRSAndFatcaFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-individual/crs-and-fatca/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteCRSAndFacta: build.mutation<{ data: CRSAndFatca }, string>({
      query: (id) => ({
        url: `/v1/questionnaires-individual/crs-and-fatca/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getApplicationClientLocks: build.query<Response<Lock>, string>({
      query: (query) => ({
        url: `/v1/locks${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getApplicationClientLockItem: build.query<{ data: Lock }, string>({
      query: (id) => ({
        url: `/v1/locks/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createApplicationClientLock: build.mutation<{ data: Lock }, LockFormValues>(
      {
        query: (body) => ({
          url: '/v1/locks',
          method: 'POST',
          body,
          signal: apiAbortController.getSignalToken(),
        }),
      },
    ),
    updateApplicationClientLock: build.mutation<
      { data: Lock },
      { body: LockFormValues; id: string }
    >({
      query: (data) => ({
        url: `/v1/locks/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getApplicationClientLockItemAuditLogs: build.query<
      Response<LockAuditLog>,
      string
    >({
      query: (query) => ({
        url: `/v1/idm-audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getApplicationRiskScore: build.query<{ data: CustomerScore }, string>({
      query: (id) => ({
        url: `/v1/clients-applications/${id}/current-score`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createApplicationRiskScore: build.mutation<
      {
        data: CustomerScoreQueuedProps;
      },
      string
    >({
      query: (id) => ({
        url: `/v1/clients-applications/${id}/clients-scores-queue-items`,
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getApplicationClientActiveLocks: build.query<Response<Lock>, string>({
      query: (id) => ({
        url: `/v1/locks?take=1&locked=true&userId=${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateApplicationClient: build.mutation<
      { data: Client },
      {
        body: {
          status: 'ACTIVE' | 'DISABLED';
        };
        id: string;
      }
    >({
      query: (data) => ({
        url: `/v1/users/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getApplicationOngoingWarning: build.query<
      { data: TransactionWarning },
      {
        appId: string;
        id: string;
        warningId: string;
      }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/${data.appId}/warnings-groups/${data.id}/warnings/${data.warningId}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getApplicationAmlUpdatingItem: build.query<
      { data: CustomerScoreQueuedProps },
      { id: string; orgId: string }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/${data.orgId}/clients-scores-queue-items/${data.id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getApplicationLastCalculatingScore: build.query<
      Response<CustomerScore>,
      string
    >({
      query: (id) => ({
        url: `/v1/clients-applications/${id}/scores`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createApplicationManualRiskScore: build.mutation<
      { data: CustomerScore },
      { id: string; totalRiskScore: number }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/${data.id}/scores`,
        method: 'POST',
        body: {
          totalRiskScore: data.totalRiskScore,
        },
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getApplicationsAMLScoresHistory: build.query<
      Response<CustomerScore>,
      { id: string; query?: string }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/${data.id}/scores${data.query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getApplicationsAMLScoresHistoryById: build.query<
      { data: CustomerScore },
      { id: string; scoreId: string }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/${data.id}/scores/${data.scoreId}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    changeIndividualApplicationDocumentStatus: build.mutation<
      any,
      { body: { ids: string[]; status: ApplicationDocumentStatus }; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/individuals/${data.id}/documents/batch-update-status`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    changeIndividualApplicationDocumentCategory: build.mutation<
      any,
      { body: { ids: string[]; documentCategoryId: string }; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/individuals/${data.id}/documents/batch-update-category`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    changeCOFApplicationDocumentStatus: build.mutation<
      any,
      { body: { ids: string[]; status: ApplicationDocumentStatus }; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/company-officials/${data.id}/documents/batch-update-status`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    changeCOFApplicationDocumentCategory: build.mutation<
      any,
      { body: { ids: string[]; documentCategoryId: string }; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-applications/company-officials/${data.id}/documents/batch-update-category`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAffilatedCompanies: build.query<Response<AffilatedCompany>, string>({
      query: (id) => ({
        url: `/v1/questionnaires-ubo/${id}/affiliated-companies?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createAffilatedCompanies: build.mutation<
      { data: AffilatedCompany },
      {
        id: string;
        body: AffilatedCompanyFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-ubo/${data.id}/affiliated-companies`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateAffilatedCompanies: build.mutation<
      { data: AffilatedCompany },
      {
        id: string;
        body: AffilatedCompanyFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-ubo/affiliated-companies/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteAffilatedCompanies: build.mutation<
      { data: AffilatedCompany },
      string
    >({
      query: (id) => ({
        url: `/v1/questionnaires-ubo/affiliated-companies/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCOFCRSAndFacta: build.query<Response<CRSAndFatca>, string>({
      query: (id) => ({
        url: `/v1/questionnaires-ubo/${id}/crs-and-fatca?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createCOFCRSAndFacta: build.mutation<
      { data: CRSAndFatca },
      {
        id: string;
        body: CRSAndFatcaFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-ubo/${data.id}/crs-and-fatca`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCOFCRSAndFacta: build.mutation<
      { data: CRSAndFatca },
      {
        id: string;
        body: CRSAndFatcaFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-ubo/crs-and-fatca/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteCOFCRSAndFacta: build.mutation<{ data: CRSAndFatca }, string>({
      query: (id) => ({
        url: `/v1/questionnaires-ubo/crs-and-fatca/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useLazyGetNewApplicationsListQuery,
  useLazyGetNewApplicationsValidatorsQuery,
  useGetNewApplicationsValidatorsQuery,
  useCreateApplicationMutation,
  useAddNewApplicationCommentMutation,
  useUpdateNewApplicationCommentMutation,
  useDeleteNewApplicationCommentMutation,
  useLazyGetNewApplicationInfoQuery,
  useUpdateNewApplicationMutation,
  useUpdateReviewStatusMutation,
  useSumSubTestReviewMutation,
  useUpdateIndividualApplicationStatusMutation,
  useLazyGetNewApplicationSystemLogsQuery,
  useUpdateCompanyOfficialApplicationMutation,
  useUpdateCompanyOfficialStatusMutation,
  useLazyGetNewApplicationCorporateInfoQuery,
  useCreateCorporateApplicationMutation,
  useCreateCompanyOfficialMutation,
  useUpdateCompanyOfficialMutation,
  useUpdateCorporateApplicationMutation,
  useUpdateCorporateApplicationStatusMutation,
  useLazyGetCorporateApplicationSystemLogsQuery,
  useUpdateCorporateReviewStatusMutation,
  useAssignCompanyOfficialMutation,
  useUpdateAppToAppMutation,
  useUpdateAppToAppStatusMutation,
  useLazyGetCustomersApplicationsListQuery,
  useLazyGetIndividualNewApplicationDocumentsQuery,
  useAddCorporateApplicationDocumentsMutation,
  useLazyGetCorporateNewApplicationDocumentsQuery,
  useLazyGetCorporateApplicationDocumentFileQuery,
  useLazyGetCorporateNewApplicationDocumentQuery,
  useChangeCorporateApplicationDocumentStatusMutation,
  useLazyGetIndividualApplicationDocumentFileQuery,
  useLazyGetIndividualNewApplicationDocumentQuery,
  useLazyGetCofApplicationDocumentFileQuery,
  useLazyGetCofNewApplicationDocumentQuery,
  useLazyGetCofNewApplicationDocumentsQuery,
  useBatchDeleteCorporateApplicationDocumentsMutation,
  useChangeCorporateApplicationDocumentCategoryMutation,
  useLazyGetApplicationAgreementsQuery,
  useLazyGetCustomersAgreementFileQuery,
  useLazyGetCustomersCurrentEmployeeQuery,
  useSetReviewEmployeeMutation,
  useLazyGetApplicationChatQuery,
  useLazyGetApplicationAgreementQuery,
  useChangeNewApplicationDocumentExpirationDateMutation,
  useLazyGetApplicationOngoingMonitoringQuery,
  useApplyApplicationOngoingWarningActionMutation,
  useBatchApplyApplicationOngoingWarningActionMutation,
  useLazyGetApplicationOngoingStatusQuery,
  useRestartOngoingCheckingMutation,
  useLazyGetNewApplicationDefaultDocumentCategoriesQuery,
  useSubmitApplicationMutation,
  useLazyGetApplicationQuestionnarieQuery,
  useUpdateCOFQuestionnarieMutation,
  useUpdateInividualQuestionnarieMutation,
  useUpdateCorporateQuestionnarieMutation,
  useRenameCofDocumentMutation,
  useRenameCorporateDocumentMutation,
  useRenameIndividualDocumentMutation,
  useLazyGetApplicationChangeLogsQuery,
  useResetApplicationWarningActionMutation,
  useResetApplicationClientPasswordMutation,
  useLazyGetNewApplicationOrganizationQuery,
  useLazyGetNewApplicationCorporateOrganizationQuery,
  useLazyGetApplicationWarningGroupWithPaginationQuery,
  useLazyGetApplicationClientAuditLogsQuery,
  useLazyGetApplicationClientLocksQuery,
  useLazyGetApplicationClientLockItemQuery,
  useCreateApplicationClientLockMutation,
  useUpdateApplicationClientLockMutation,
  useLazyGetApplicationClientLockItemAuditLogsQuery,
  useLazyGetApplicationClientActiveLocksQuery,
  useUpdateApplicationClientMutation,
  useLazyGetApplicationOngoingWarningQuery,
  useLazyGetApplicationRiskScoreQuery,
  useCreateApplicationRiskScoreMutation,
  useLazyGetApplicationAmlUpdatingItemQuery,
  useLazyGetApplicationLastCalculatingScoreQuery,
  useCreateApplicationManualRiskScoreMutation,
  useLazyGetApplicationsAMLScoresHistoryByIdQuery,
  useLazyGetApplicationsAMLScoresHistoryQuery,
  useLazyGetRelationShipToCustomerDictionaryQuery,
  useLazyGetSourceOfWealthForCorporateDictionaryQuery,
  useLazyGetBusinessActivitiesDictionaryQuery,
  useLazyGetCustomersMainClientsDictionaryQuery,
  useLazyGetSalesChannelsDictionaryQuery,
  useLazyGetCustomerSourceOfWealthQuery,
  useLazyGetCustomerSourceOfFundsQuery,
  useLazyGetAnnualIncomeDictionaryQuery,
  useLazyGetTypeOfAccountsDictionaryQuery,
  useLazyGetAccountsCurrenciesQuery,
  useCreateAccountsCurrencyMutation,
  useUpdateAccountsCurrencyMutation,
  useDeleteAccountsCurrencyMutation,
  useLazyGetAccountsFlowsQuery,
  useCreateAccountsFlowMutation,
  useUpdateAccountsFlowMutation,
  useDeleteAccountsFlowMutation,
  useLazyGetExpectedFundsOriginsQuery,
  useCreateExpectedFundsOriginsMutation,
  useUpdateExpectedFundsOriginsMutation,
  useDeleteExptectedFundsOriginsMutation,
  useLazyGetSourceOfWealthForIndividualDictionaryQuery,
  useLazyGetSourceOfIncomeDictionaryQuery,
  useLazyGetIndividualAccountsCurrenciesQuery,
  useCreateIndividualAccountsCurrencyMutation,
  useUpdateIndividualAccountsCurrencyMutation,
  useDeleteIndividualAccountsCurrencyMutation,
  useLazyGetIndividualAccountsFlowsQuery,
  useCreateIndividualAccountsFlowMutation,
  useUpdateIndividualAccountsFlowMutation,
  useDeleteIndividualAccountsFlowMutation,
  useLazyGetIndividualExpectedFundsOriginsQuery,
  useCreateIndividualExpectedFundsOriginsMutation,
  useUpdateIndividualExpectedFundsOriginsMutation,
  useDeleteIndividualExptectedFundsOriginsMutation,
  useLazyGetCRSAndFactaQuery,
  useCreateCRSAndFactaMutation,
  useUpdateCRSAndFactaMutation,
  useDeleteCRSAndFactaMutation,
  useAddIndividualApplicationDocumentsMutation,
  useChangeIndividualApplicationDocumentStatusMutation,
  useChangeIndividualApplicationDocumentCategoryMutation,
  useBatchDeleteIndividualApplicationDocumentsMutation,
  useAddCofApplicationDocumentsMutation,
  useBatchDeleteCOFApplicationDocumentsMutation,
  useChangeCOFApplicationDocumentCategoryMutation,
  useChangeCOFApplicationDocumentStatusMutation,
  useLazyGetAffilatedCompaniesQuery,
  useUpdateAffilatedCompaniesMutation,
  useCreateAffilatedCompaniesMutation,
  useDeleteAffilatedCompaniesMutation,
  useLazyGetCOFCRSAndFactaQuery,
  useCreateCOFCRSAndFactaMutation,
  useUpdateCOFCRSAndFactaMutation,
  useDeleteCOFCRSAndFactaMutation,
} = applicationsApi;

export const {
  endpoints: {
    getNewApplicationsList,
    getNewApplicationsValidators,
    createApplication,
    addNewApplicationComment,
    updateNewApplicationComment,
    deleteNewApplicationComment,
    getNewApplicationInfo,
    updateNewApplication,
    sumSubTestReview,
    updateIndividualApplicationStatus,
    getNewApplicationSystemLogs,
    updateReviewStatus,
    updateCompanyOfficialApplication,
    updateCompanyOfficialStatus,
    getNewApplicationCorporateInfo,
    createCorporateApplication,
    updateCorporateApplication,
    createCompanyOfficial,
    updateCorporateApplicationStatus,
    getCorporateApplicationSystemLogs,
    updateCorporateReviewStatus,
    updateCompanyOfficial,
    assignCompanyOfficial,
    updateAppToApp,
    updateAppToAppStatus,
    getCustomersApplicationsList,
    getIndividualNewApplicationDocuments,
    addCorporateApplicationDocuments,
    getCorporateNewApplicationDocuments,
    getCorporateApplicationDocumentFile,
    getCorporateNewApplicationDocument,
    changeCorporateApplicationDocumentStatus,
    getIndividualApplicationDocumentFile,
    getIndividualNewApplicationDocument,
    getCofApplicationDocumentFile,
    getCofNewApplicationDocument,
    getCofNewApplicationDocuments,
    batchDeleteCorporateApplicationDocuments,
    changeCorporateApplicationDocumentCategory,
    getApplicationAgreements,
    getCustomersAgreementFile,
    getCustomersCurrentEmployee,
    setReviewEmployee,
    getApplicationChat,
    changeNewApplicationDocumentExpirationDate,
    getApplicationOngoingMonitoring,
    getApplicationOngoingStatus,
    restartOngoingChecking,
    submitApplication,
    getNewApplicationDefaultDocumentCategories,
    getApplicationQuestionnarie,
    updateCOFQuestionnarie,
    updateInividualQuestionnarie,
    updateCorporateQuestionnarie,
    renameCofDocument,
    renameCorporateDocument,
    renameIndividualDocument,
    resetApplicationWarningAction,
    getNewApplicationOrganization,
    getNewApplicationCorporateOrganization,
    getApplicationWarningGroupWithPagination,
    getApplicationClientAuditLogs,
    getApplicationClientLocks,
    getApplicationClientLockItem,
    createApplicationClientLock,
    updateApplicationClientLock,
    getApplicationClientLockItemAuditLogs,
    getApplicationClientActiveLocks,
    updateApplicationClient,
    getApplicationOngoingWarning,
    getApplicationRiskScore,
    createApplicationRiskScore,
    getApplicationAmlUpdatingItem,
    getApplicationLastCalculatingScore,
    createApplicationManualRiskScore,
    getApplicationsAMLScoresHistoryById,
    getApplicationsAMLScoresHistory,
    getRelationShipToCustomerDictionary,
    getSourceOfWealthForCorporateDictionary,
    getBusinessActivitiesDictionary,
    getCustomersMainClientsDictionary,
    getSalesChannelsDictionary,
    getCustomerSourceOfWealth,
    getCustomerSourceOfFunds,
    getAnnualIncomeDictionary,
    getTypeOfAccountsDictionary,
    getAccountsCurrencies,
    updateAccountsCurrency,
    deleteAccountsCurrency,
    createAccountsCurrency,
    getAccountsFlows,
    createAccountsFlow,
    updateAccountsFlow,
    deleteAccountsFlow,
    getExpectedFundsOrigins,
    createExpectedFundsOrigins,
    updateExpectedFundsOrigins,
    deleteExptectedFundsOrigins,
    getSourceOfWealthForIndividualDictionary,
    getSourceOfIncomeDictionary,
    getIndividualAccountsCurrencies,
    updateIndividualAccountsCurrency,
    deleteIndividualAccountsCurrency,
    createIndividualAccountsCurrency,
    getIndividualAccountsFlows,
    createIndividualAccountsFlow,
    updateIndividualAccountsFlow,
    deleteIndividualAccountsFlow,
    getIndividualExpectedFundsOrigins,
    createIndividualExpectedFundsOrigins,
    updateIndividualExpectedFundsOrigins,
    deleteIndividualExptectedFundsOrigins,
    getCRSAndFacta,
    createCRSAndFacta,
    updateCRSAndFacta,
    deleteCRSAndFacta,
    addIndividualApplicationDocuments,
    changeIndividualApplicationDocumentStatus,
    changeIndividualApplicationDocumentCategory,
    batchDeleteIndividualApplicationDocuments,
    addCofApplicationDocuments,
    batchDeleteCOFApplicationDocuments,
    changeCOFApplicationDocumentCategory,
    changeCOFApplicationDocumentStatus,
    getAffilatedCompanies,
    createAffilatedCompanies,
    updateAffilatedCompanies,
    deleteAffilatedCompanies,
    getCOFCRSAndFacta,
    createCOFCRSAndFacta,
    updateCOFCRSAndFacta,
    deleteCOFCRSAndFacta,
  },
} = applicationsApi;
